import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  Button
} from '@mui/material';
import {
  Logout,
  Person,
  AccountCircle,
  Settings,
  Notifications,
  People,
  Receipt
} from '@mui/icons-material';

function Navbar({ user, onLogout }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'doctor': return 'secondary';
      case 'reception': return 'info';
      case 'finance': return 'success';
      case 'coordinator': return 'success';
      case 'admin': return 'error';
      default: return 'default';
    }
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'doctor': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'reception': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      case 'coordinator': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
      case 'admin': return 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <AppBar
      position="sticky"
      elevation={3}
      sx={{
        background: 'linear-gradient(90deg, #2e7d32 0%, #1976d2 100%)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <Toolbar sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="MSG Logo"
            sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '50%', background: 'white', p: '2px' }}
          />
          <Box>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                letterSpacing: 0.5,
                display: { xs: 'none', md: 'block' }
              }}
            >
              MSG Naturopathy Center
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                display: { xs: 'none', md: 'block' }
              }}
            >
              Management System
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Patient List Button - Reception only (Doctor has Patient Records tab in dashboard) */}
          {user.role === 'reception' && (
            <Button
              variant="outlined"
              startIcon={<People />}
              onClick={() => navigate(`/${user.role}/patients`)}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              Patient Records
            </Button>
          )}

          {/* Billing Button - Reception + Finance */}
          {(user.role === 'reception' || user.role === 'finance') && (
            <Button
              variant="outlined"
              startIcon={<Receipt />}
              onClick={() => navigate(user.role === 'finance' ? '/finance/billing' : '/reception/billing')}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              {user.role === 'finance' ? 'Finance' : 'Billing'}
            </Button>
          )}


          {/* Notifications Icon */}
          <Tooltip title="Notifications">
            <IconButton
              size="large"
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Info - Desktop */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 0.5,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
                {user.fullName}
              </Typography>
              <Chip
                label={user.role.toUpperCase()}
                size="small"
                color={getRoleColor(user.role)}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  mt: 0.5
                }}
              />
            </Box>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: getRoleGradient(user.role),
                fontWeight: 700,
                cursor: 'pointer',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)'
                }
              }}
              onClick={handleMenuClick}
            >
              {getInitials(user.fullName)}
            </Avatar>
          </Box>

          {/* User Menu Button - Mobile */}
          <Tooltip title="Account">
            <IconButton
              onClick={handleMenuClick}
              size="large"
              sx={{
                display: { xs: 'flex', sm: 'none' },
                color: 'white'
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: getRoleGradient(user.role),
                  fontSize: '0.9rem',
                  fontWeight: 700
                }}
              >
                {getInitials(user.fullName)}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 220,
              mt: 1.5,
              borderRadius: 2,
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          {/* User Info in Menu */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: getRoleGradient(user.role),
                  fontWeight: 700
                }}
              >
                {getInitials(user.fullName)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.username}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={user.role.toUpperCase()}
              size="small"
              color={getRoleColor(user.role)}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Divider />

          {/* Menu Items */}
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Profile</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Logout fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
